import React, {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';

import { cn } from 'common/style/utils';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import { Separator } from 'common/ui/Separator';
import Badge from 'common/uiV3/Badge';
import { forwardRefNoClassName } from 'common/uiV3/common';
import Input from 'common/uiV3/Input';
import {
  type SelectBaseOption,
  type SelectContentProps,
  SelectContext,
  type SelectContextValue,
  type SelectCurrentValueProps,
  type SelectCustomTriggerProps,
  type SelectOptionItem,
  type SelectProps,
  type SelectTriggerProps,
  type StyleVariants,
  selectVariants,
  useSelectContext,
} from 'common/uiV3/Select/common';
import {
  filterSelectOptionForSearch,
  filterSelectOptionsByValueOrSearchLabel,
  getFlattenedOptions,
  getOptionLabelByValue,
} from 'common/uiV3/Select/utils';
import Spinner from 'common/uiV3/Spinner';

const Select = ({
  isMultiSelect = false,
  value,
  defaultValue,
  onValueChange,
  variant,
  size,
  fullWidth = false,
  sizeFull = false,
  allowClear = false,
  isSearchable = false,
  onSearchChange,
  findMatch,
  cta,
  disabled = false,
  position = 'popper',
  maxVisible = 3,
  children,
  placeholder = 'Select',
  fitTrigger,
  placeholderVariant,
  options,
  renderBadge,
  hideChevron = false,
  isLoading = false,
  hideSelection = false,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...contentProps
}: SelectProps) => {
  // Validate children - if provided, must be Select.CustomTrigger
  if (children && isValidElement(children)) {
    const childType = children.type as React.ComponentType<any>;
    if (childType !== SelectCustomTrigger) {
      throw new Error('Select children must be Select.CustomTrigger when provided');
    }
  }

  const selectedOptionsCache = useRef<Map<string, SelectBaseOption>>(new Map());

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string | string[] | undefined>(
    value ??
      (isMultiSelect ? ((defaultValue as string[]) ?? []) : (defaultValue as string | undefined)),
  );

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, internalValue]);

  useEffect(() => {
    const flattenedOptions = getFlattenedOptions(options);
    flattenedOptions.forEach((option) => {
      selectedOptionsCache.current.set(option.value, option);
    });
  }, [options]);

  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = useCallback(
    (newValue: string | string[] | undefined) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }

      if (isMultiSelect) {
        (onValueChange as ((value: string[]) => void) | undefined)?.(newValue as string[]);
      } else {
        (onValueChange as ((value: string) => void) | undefined)?.(newValue as string);
      }
    },
    [value, onValueChange, isMultiSelect],
  );

  const handleClearValue = useCallback(() => {
    if (isMultiSelect) {
      handleValueChange([]);
    } else {
      handleValueChange(undefined);
    }
  }, [isMultiSelect, handleValueChange]);

  const handleToggleValue = useCallback(
    (optionValue: string) => {
      if (cta && optionValue === cta.option.value) {
        cta.onClick();
        setOpen(false);
        return;
      }

      if (!isMultiSelect) {
        if (optionValue === currentValue) {
          handleValueChange(undefined);
        } else {
          handleValueChange(optionValue);
        }

        setOpen(false);
        return;
      }

      const currentArray = (currentValue || []) as string[];

      const newValues = currentArray.includes(optionValue)
        ? currentArray.filter((v) => v !== optionValue)
        : [...currentArray, optionValue];

      handleValueChange(newValues);
    },
    [currentValue, isMultiSelect, handleValueChange, cta],
  );

  const styleVariants: StyleVariants = useMemo(
    () => ({
      size,
      variant,
      fullWidth,
      sizeFull,
      isMultiSelect,
      position,
      placeholderVariant,
      fitTrigger,
    }),
    [size, variant, fullWidth, sizeFull, isMultiSelect, position, placeholderVariant, fitTrigger],
  );

  const optionsWithCta = useMemo(() => {
    if (cta) {
      return [...options, cta.option];
    }
    return options;
  }, [options, cta]);

  const contextValue = useMemo<SelectContextValue>(
    // @ts-expect-error - isMultiSelect is always true
    () => ({
      open,
      allowClear,
      value: currentValue,
      onClearValue: handleClearValue,
      onValueChange: handleValueChange,
      onToggleValue: handleToggleValue,
      isSearchable,
      onSearchChange,
      findMatch,
      isMultiSelect,
      maxVisible,
      styleVariants,
      options: optionsWithCta,
      hideChevron,
      isLoading,
      selectedOptionsCache,
    }),
    [
      open,
      allowClear,
      currentValue,
      handleValueChange,
      handleToggleValue,
      handleClearValue,
      isSearchable,
      onSearchChange,
      findMatch,
      isMultiSelect,
      maxVisible,
      styleVariants,
      optionsWithCta,
      hideChevron,
      isLoading,
    ],
  );

  // Provide default children if none are provided
  const defaultChildren = !children ? (
    <SelectTrigger aria-invalid={!!ariaInvalid} aria-required={!!ariaRequired}>
      <SelectCurrentValue
        placeholder={placeholder}
        renderBadge={renderBadge}
        hideSelection={hideSelection}
      />
    </SelectTrigger>
  ) : (
    children
  );

  return (
    <SelectContext.Provider value={contextValue}>
      <SelectPrimitive.Root
        open={open}
        value={isMultiSelect ? '' : (currentValue as string)}
        onOpenChange={setOpen}
        disabled={disabled}>
        {defaultChildren}
        <SelectContent {...contentProps} position={position} />
      </SelectPrimitive.Root>
    </SelectContext.Provider>
  );
};

const SelectCurrentValue = ({
  placeholder = 'Select',
  renderBadge,
  hideSelection = false,
}: SelectCurrentValueProps) => {
  const {
    value,
    isMultiSelect,
    styleVariants,
    maxVisible,
    onValueChange,
    options,
    selectedOptionsCache,
  } = useSelectContext();
  const styles = selectVariants(styleVariants);

  const handleRemoveValue = useCallback(
    (optionValue: string) => {
      if (!isMultiSelect) {
        return;
      }

      const currentArray = (value || []) as string[];
      const newValues = currentArray.filter((v) => v !== optionValue);
      onValueChange?.(newValues);
    },
    [value, isMultiSelect, onValueChange],
  );

  const selectedOptions = useMemo(() => {
    if (!isMultiSelect) {
      return [];
    }

    const selectedValues = (value || []) as string[];
    const flattenedOptions = getFlattenedOptions(options);

    return selectedValues
      .map((selectedValue) => {
        const foundInOptions = flattenedOptions.find((option) => option.value === selectedValue);
        if (foundInOptions) {
          return foundInOptions;
        }
        return selectedOptionsCache.current.get(selectedValue);
      })
      .filter((option): option is SelectBaseOption => option !== undefined);
  }, [options, value, isMultiSelect, selectedOptionsCache]);

  const handleRenderBadge = useCallback(
    (option: SelectBaseOption) => {
      if (renderBadge) {
        return renderBadge?.(option, () => handleRemoveValue(option.value));
      }
      return (
        <Badge key={option.value} variant="info" onClose={() => handleRemoveValue(option.value)}>
          {option.label}
        </Badge>
      );
    },
    [renderBadge, handleRemoveValue],
  );

  const selectedOptionLabel = useMemo(() => {
    if (isMultiSelect || !value) {
      return undefined;
    }

    const labelFromOptions = getOptionLabelByValue(options, value as string);
    if (labelFromOptions) {
      return labelFromOptions;
    }

    const cachedOption = selectedOptionsCache.current.get(value as string);
    return cachedOption?.label;
  }, [options, value, isMultiSelect, selectedOptionsCache]);

  if (
    !value ||
    (!isMultiSelect && !selectedOptionLabel) ||
    (isMultiSelect && selectedOptions.length === 0)
  ) {
    return <div className={styles.placeholder()}>{placeholder}</div>;
  }

  if (hideSelection) {
    return (
      <div className={cn('text-foreground truncate')}>
        {value.length} option{value.length > 1 && 's'} selected
      </div>
    );
  }

  if (isMultiSelect && selectedOptions.length > 0) {
    return (
      <Flex className={styles.selectedContainer()}>
        {selectedOptions.slice(0, maxVisible).map((option) => handleRenderBadge(option))}
        {maxVisible && selectedOptions.length > maxVisible && (
          <Badge variant="info">+{selectedOptions.length - maxVisible} more</Badge>
        )}
      </Flex>
    );
  }

  return <Flex className={styles.itemLabel()}>{selectedOptionLabel}</Flex>;
};

const SelectContent = ({
  position,
  searchPlaceholder = 'Search',
  emptyPlaceholder = 'No results found',
  ...props
}: SelectContentProps) => {
  const {
    styleVariants,
    isSearchable,
    onSearchChange,
    isLoading,
    findMatch,
    onToggleValue,
    open,
    options,
  } = useSelectContext();

  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { content, viewport } = selectVariants(styleVariants);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (!open) {
      setFilteredOptions(options);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    }
  }, [open, options]);

  const selectableOptions = useMemo(
    () => getFlattenedOptions(filteredOptions).filter((option) => !option.disabled),
    [filteredOptions],
  );

  const handleSearchChange = async (search: string) => {
    if (onSearchChange) {
      onSearchChange(search);
    } else {
      const filterFunction = findMatch || filterSelectOptionsByValueOrSearchLabel;
      setFilteredOptions(filterFunction(filterSelectOptionForSearch(options), search));
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < selectableOptions.length - 1 ? prev + 1 : 0));
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : selectableOptions.length - 1));
          break;

        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < selectableOptions.length) {
            const selectedOption = selectableOptions[selectedIndex];
            onToggleValue?.(selectedOption.value);
          }
          break;

        case 'Escape':
          break;

        default:
          if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
            searchInputRef.current.focus();
          }
          break;
      }
    },
    [selectedIndex, selectableOptions, onToggleValue],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return;
  }, [handleKeyDown, open]);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={contentRef}
        className={content()}
        position={position}
        {...props}
        onKeyDown={(e) => {
          if (isSearchable && ['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
            e.preventDefault();
          }
        }}>
        {isSearchable && (
          <SelectSearchInput
            handleSearchChange={handleSearchChange}
            searchPlaceholder={searchPlaceholder}
          />
        )}
        <SelectPrimitive.Viewport className={cn(viewport())}>
          {isLoading && (
            <Flex className="justify-center items-center h-full py-3">
              <Spinner size="large" />
            </Flex>
          )}
          {!isLoading && filteredOptions.length > 0 && (
            <SelectOptionsList
              options={filteredOptions}
              onToggleValue={onToggleValue}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
            />
          )}
          {!isLoading && filteredOptions.length === 0 && (
            <Flex className="justify-center items-center h-full py-3">{emptyPlaceholder}</Flex>
          )}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const SelectSearchInput = forwardRef<
  HTMLInputElement,
  {
    searchPlaceholder: string;
    handleSearchChange: (search: string) => void;
  }
>(({ searchPlaceholder, handleSearchChange }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { styleVariants } = useSelectContext();
  const { searchContainer, searchContainerInner } = selectVariants(styleVariants);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, []);

  return (
    <div className={searchContainer()}>
      <div className={searchContainerInner()}>
        <Input
          ref={inputRef}
          placeholder={searchPlaceholder}
          startIcon={SearchIcon}
          variant="unstyled"
          autoFocus
          onChange={handleSearchChange}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
});

const SelectOptionsList = forwardRefNoClassName<
  HTMLDivElement,
  {
    options: SelectOptionItem[];
    onToggleValue?: (value: string) => void;
    selectedIndex?: number;
    setSelectedIndex?: (index: number) => void;
  }
>((props, ref) => {
  let currentSelectableIndex = 0;

  return (
    <div ref={ref}>
      {props.options.map((option, index) => {
        if (option.type === 'group') {
          return (
            <SelectGroup key={`${option.type}-${index}`}>
              <SelectLabel>{option.groupLabel}</SelectLabel>
              {option.options.map((subOption) => {
                const isHighlighted =
                  !subOption.disabled && currentSelectableIndex === props.selectedIndex;

                if (!subOption.disabled) {
                  currentSelectableIndex++;
                }

                return (
                  <SelectOption
                    key={`${option.type}-${index}-${subOption.value}`}
                    option={subOption}
                    onToggleValue={props.onToggleValue}
                    isHighlighted={isHighlighted}
                    setSelectedIndex={props.setSelectedIndex}
                    selectableIndex={currentSelectableIndex - 1}
                  />
                );
              })}
            </SelectGroup>
          );
        }

        if (option.type === 'separator') {
          return <SelectSeparator key={`${option.type}-${index}`} />;
        }

        const isHighlighted = !option.disabled && currentSelectableIndex === props.selectedIndex;

        if (!option.disabled) {
          currentSelectableIndex++;
        }

        return (
          <SelectOption
            key={option.value}
            option={option}
            onToggleValue={props.onToggleValue}
            isHighlighted={isHighlighted}
            setSelectedIndex={props.setSelectedIndex}
            selectableIndex={currentSelectableIndex - 1}
          />
        );
      })}
    </div>
  );
});

const SelectOption = forwardRefNoClassName<
  HTMLDivElement,
  {
    option: SelectBaseOption;
    onToggleValue?: (value: string) => void;
    isHighlighted?: boolean;
    setSelectedIndex?: (index: number) => void;
    selectableIndex?: number;
  }
>((props, ref) => {
  const { styleVariants, value, isMultiSelect } = useSelectContext();
  const { item, itemIndicator, itemLabel } = selectVariants(styleVariants);

  const isSelected = isMultiSelect
    ? ((value as string[]) || []).includes(props.option.value)
    : (value as string) === props.option.value;

  return (
    <Flex
      ref={ref}
      className={item()}
      data-state={isSelected ? 'checked' : 'unchecked'}
      data-highlighted={props.isHighlighted}
      data-disabled={props.option.disabled}
      onMouseEnter={() => props.setSelectedIndex?.(props.selectableIndex ?? 0)}
      onPointerUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onClick={() => props.onToggleValue?.(props.option.value)}>
      <Flex className={itemLabel()}>{props.option.label}</Flex>
      {isSelected && (
        <span className={itemIndicator()}>
          <CheckIcon className="size-3.5" />
        </span>
      )}
    </Flex>
  );
});

const SelectGroup = forwardRefNoClassName<
  React.ElementRef<typeof SelectPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>
>((props, ref) => {
  return <SelectPrimitive.Group data-slot="select-group" {...props} ref={ref} />;
});

const SelectClearValue = forwardRefNoClassName<HTMLDivElement>((_, ref) => {
  const { onClearValue } = useSelectContext();

  return (
    <Flex
      ref={ref}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClearValue?.();
      }}>
      <XIcon data-trigger-icon className="size-3.5 pr-0.5" />
    </Flex>
  );
});

const SelectTrigger = forwardRefNoClassName<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ children = false, ...props }, ref) => {
  const { styleVariants, value, allowClear, hideChevron } = useSelectContext();
  const styles = selectVariants(styleVariants);

  const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
  const showClearValue = hasValue && allowClear;
  const showRightActions = showClearValue || !hideChevron;

  return (
    <SelectPrimitive.Trigger ref={ref} className={styles.trigger()} {...props}>
      {children}
      {showRightActions && (
        <HStack className="gap-1 items-center">
          {showClearValue && <SelectClearValue />}
          {!hideChevron && showClearValue && (
            <Separator orientation="vertical" className="w-1 min-h-4" />
          )}
          {!hideChevron && (
            <SelectPrimitive.Icon asChild>
              <ChevronDownIcon data-trigger-icon className="size-4" />
            </SelectPrimitive.Icon>
          )}
        </HStack>
      )}
    </SelectPrimitive.Trigger>
  );
});

const SelectCustomTrigger = forwardRefNoClassName<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectCustomTriggerProps
>(({ children, ...props }, ref) => {
  const className = props.asChild ? {} : { className: 'unstyled-button outline-none' };

  return (
    <SelectPrimitive.Trigger {...props} {...className} ref={ref}>
      {children}
    </SelectPrimitive.Trigger>
  );
});

const SelectLabel = forwardRefNoClassName<React.ElementRef<typeof SelectPrimitive.Label>>(
  (props, ref) => {
    const { styleVariants } = useSelectContext();
    const styles = selectVariants(styleVariants);

    return <SelectPrimitive.Label className={styles.label()} {...props} ref={ref} />;
  },
);

const SelectSeparator = forwardRefNoClassName<React.ElementRef<typeof SelectPrimitive.Separator>>(
  (props, ref) => {
    const { styleVariants } = useSelectContext();
    const styles = selectVariants(styleVariants);

    return <SelectPrimitive.Separator className={styles.separator()} {...props} ref={ref} />;
  },
);

Select.CustomTrigger = SelectCustomTrigger;

const SelectOptionLabel = forwardRefNoClassName<HTMLDivElement>((props, ref) => {
  return <Flex className="items-center gap-2" {...props} ref={ref} />;
});

Select.OptionLabel = SelectOptionLabel;

export default Select;
